/*
 * BUZZWOO - Image-with-text desktop clamp
 *
 * On desktop (>= 750px), clamps the section's TEXT block so the whole content
 * (heading + text) fits within the rendered height of the image, and shows a
 * "Mehr anzeigen" toggle only when the text overflows. The text block is a
 * plain block element (the content container is a centered flexbox, which
 * cannot be clamped without clipping the heading). On mobile (stacked) the
 * text is never clamped. Progressive enhancement: without JS the full text
 * renders normally.
 */
if (!customElements.get('bw-image-text-clamp')) {
  class BwImageTextClamp extends HTMLElement {
    constructor() {
      super();
      this.mql = window.matchMedia('(min-width: 750px)');
      this.expanded = false;
      this.BUTTON_RESERVE = 36; // px reserved for the toggle line
      this.MIN_TEXT = 48; // px — below this the image is too short to clamp usefully
      this._onResize = this._onResize.bind(this);
      this._onToggle = this._onToggle.bind(this);
    }

    connectedCallback() {
      this.media = this.querySelector('.image-with-text__media');
      this.content = this.querySelector('.image-with-text__content');
      // The body text block, NOT the caption.
      this.text = this.content
        ? this.content.querySelector('.image-with-text__text:not(.image-with-text__text--caption)')
        : null;
      if (!this.media || !this.content || !this.text) return;

      this.moreLabel = this.dataset.moreLabel || 'Mehr anzeigen';
      this.lessLabel = this.dataset.lessLabel || 'Weniger anzeigen';
      if (!this.text.id) this.text.id = (this.content.id || 'bw-itc') + '-text';

      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'bw-itc__toggle';
      this.button.hidden = true;
      this.button.setAttribute('aria-controls', this.text.id);
      this.button.addEventListener('click', this._onToggle);
      this.text.insertAdjacentElement('afterend', this.button);

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this._onResize);
        this.ro.observe(this.media);
      }
      window.addEventListener('resize', this._onResize);

      const img = this.media.querySelector('img');
      if (img && !img.complete) {
        img.addEventListener('load', this._onResize, { once: true });
      }
      requestAnimationFrame(() => this._apply());
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      if (this.ro) this.ro.disconnect();
    }

    _onResize() {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(() => this._apply());
    }

    _apply() {
      if (!this.button) return;

      // Reset to natural so we measure unclamped.
      this._collapse(false);
      this.button.hidden = true;

      // Mobile (stacked): never clamp.
      if (!this.mql.matches) {
        this.expanded = false;
        return;
      }

      const imageHeight = Math.round(this.media.getBoundingClientRect().height);
      if (!imageHeight) return;

      const contentHeight = this.content.scrollHeight;
      if (contentHeight <= imageHeight + 4) {
        // Content already fits the image height.
        this.expanded = false;
        return;
      }

      const textHeight = this.text.scrollHeight;
      // Shrink the text by the content's overflow, leaving room for the toggle.
      this._textMax = textHeight - (contentHeight - imageHeight) - this.BUTTON_RESERVE;
      if (this._textMax < this.MIN_TEXT) {
        // Image too short relative to the heading to clamp usefully.
        this.expanded = false;
        return;
      }

      this.button.hidden = false;
      if (this.expanded) {
        this.button.textContent = this.lessLabel;
        this.button.setAttribute('aria-expanded', 'true');
      } else {
        this._collapse(true);
      }
    }

    _collapse(on) {
      if (on) {
        this.text.style.maxHeight = this._textMax + 'px';
        this.text.classList.add('bw-itc--collapsed');
        if (this.button) {
          this.button.textContent = this.moreLabel;
          this.button.setAttribute('aria-expanded', 'false');
        }
      } else {
        this.text.style.maxHeight = '';
        this.text.classList.remove('bw-itc--collapsed');
      }
    }

    _onToggle() {
      this.expanded = !this.expanded;
      if (this.expanded) {
        this._collapse(false);
        this.button.textContent = this.lessLabel;
        this.button.setAttribute('aria-expanded', 'true');
      } else {
        this._collapse(true);
      }
    }
  }

  customElements.define('bw-image-text-clamp', BwImageTextClamp);
}
